import { useMemo } from 'react';

import type { ContentfulAsset, ContentfulEntry } from '@order/Contentful';
import { useContentfulContentTypeEntry } from '@order/Contentful';

import type { SweetpassBenefitScreenUpgradeCtaPalette } from './SweetpassBenefitScreenPalette';

export const useBenefitsScreenLoadedContent = (
  tag?: SweetpassBenefitsPageTag | '',
) => {
  const { data: contentfulData, fetching: isLoading } =
    useContentfulContentTypeEntry<ContentfulSweetpassBenefitsPage>({
      contentType: 'sweetpassBenefitsPage',
      tags: tag ? [tag] : [],
      include: 3,
    });

  const content = useMemo(
    () => ({
      title: contentfulData?.fields?.title,
      content: contentfulData?.fields?.content?.map?.(contentFromContentful),
    }),
    [contentfulData],
  );

  return { content, isLoading };
};

// ––– TYPES –––––––––––––––––––––––––

export type SweetpassBenefitsPageTag =
  | 'sweetpass-benefits-page'
  | 'sweetpass-plus-benefits-page';

export type SweetpassBenefitsPageBenefitCtaHandler = (
  action: SweetpassBenefitsBenefit['action'],
) => void;

export type SweetpassBenefitsPageUpgradeCtaHandler = (
  action: SweetpassBenefitsCta['action'],
) => void;

export type SweetpassBenefitsPageCardCtaHandler = (
  action: SweetpassBenefitsCardCta['action'],
) => void;

// ––– Benefits Page –––––––––––––––––––––––––

export type SweetpassBenefitsPage = {
  readonly title: string;
  readonly content: readonly SweetpassBenefitsContent[];
};

type ContentfulSweetpassBenefitsPage = {
  readonly title: string;
  readonly content: ReadonlyArray<
    ContentfulEntry<ContentfulSweetpassBenefitsSection>
  >;
};

// ––– Content (Benefit List, CTA, Cards) ––––

export type SweetpassBenefitsContent =
  | SweetpassBenefitsCta
  | SweetpassBenefitList
  | SweetpassBenefitsCardsSection
  | SweetpassUpgradeUnknownContent;

type ContentfulSweetpassBenefitsSection =
  | ContentfulSweetpassBenefitsBenefitList
  | ContentfulSweetpassBenefitsCta
  | ContentfulSweetpassBenefitsCards;

export type SweetpassUpgradeUnknownContent = {
  readonly type: string;
  readonly id: string;
};

const contentFromContentful = (
  entry: ContentfulEntry<ContentfulSweetpassBenefitsSection>,
): SweetpassBenefitsContent => {
  const contentTypeId = entry.sys.contentType.sys.id;

  if (contentTypeId === 'sweetpassBenefitsList') {
    return benefitListFromContentful(
      entry as ContentfulEntry<ContentfulSweetpassBenefitsBenefitList>,
    );
  }

  if (contentTypeId === 'sweetpassBenefitsPageCta') {
    return ctaFromContentful(
      entry as ContentfulEntry<ContentfulSweetpassBenefitsCta>,
    );
  }

  if (contentTypeId === 'sweetpassBenefitsCardsSection') {
    return cardsSectionFromContentful(
      entry as ContentfulEntry<ContentfulSweetpassBenefitsCards>,
    );
  }

  return {
    type: contentTypeId,
    id: entry.sys.id,
  };
};

// ––– CTA –––––––––––––––––––––––––––––––––––––––––––––––

export type SweetpassBenefitsUpgradeCtaAction = 'upgrade_to_sweetpass_plus';

export type SweetpassBenefitsCta = {
  readonly type: 'sweetpassBenefitsCta';
  readonly id: string;
  readonly title: string;
  readonly action: SweetpassBenefitsUpgradeCtaAction;
  readonly palette: SweetpassBenefitScreenUpgradeCtaPalette;
};

type ContentfulSweetpassBenefitsCta = {
  readonly title: string;
  readonly action: SweetpassBenefitsCta['action'];
  readonly palette: string;
};

const ctaFromContentful = (
  entry: ContentfulEntry<ContentfulSweetpassBenefitsCta>,
): SweetpassBenefitsCta => ({
  type: 'sweetpassBenefitsCta',
  id: entry.sys.id,
  title: entry.fields.title.replace('{price}', '$10/month'), // TODO: How to dynamically interpolate this in the future?
  action: entry.fields.action as SweetpassBenefitsUpgradeCtaAction,
  palette: entry.fields.palette as SweetpassBenefitScreenUpgradeCtaPalette,
});

// ––– Benefit List ––––––––––––––––––––––––––––

export type SweetpassBenefitsPageBenefitListPaletteTag =
  | 'regular'
  | 'greyed-out';

export type SweetpassBenefitList = {
  readonly type: 'sweetpassBenefitsList';
  readonly id: string;
  readonly title: string;
  readonly benefits: readonly SweetpassBenefitsBenefit[];
  readonly palette: SweetpassBenefitsPageBenefitListPaletteTag;
};

type ContentfulSweetpassBenefitsBenefitList = {
  readonly title: string;
  readonly benefitsList: ReadonlyArray<
    ContentfulEntry<ContentfulSweetpassUpgradeBenefit>
  >;
  readonly palette: string;
};

const benefitListFromContentful = (
  entry: ContentfulEntry<ContentfulSweetpassBenefitsBenefitList>,
): SweetpassBenefitList => ({
  type: 'sweetpassBenefitsList',
  id: entry.sys.id,
  title: entry.fields.title,
  benefits: entry.fields.benefitsList.map(benefitFromContentful),
  palette: entry.fields.palette as SweetpassBenefitsPageBenefitListPaletteTag,
});

// ––– Benefit ––––––––––––––––––––––––––––––––––

export type SweetpassBenefitsBenefitAction = 'challenges-rewards' | null;

export type SweetpassBenefitsBenefit = {
  readonly type: 'sweetpassBenefitsBenefit';
  readonly id: string;
  readonly image: {
    readonly url: string;
    readonly description: string;
  };
  readonly titleText: string;
  readonly bodyText: string;
  readonly action?: SweetpassBenefitsBenefitAction;
};

type ContentfulSweetpassUpgradeBenefit = {
  readonly image: ContentfulAsset;
  readonly titleText: string;
  readonly bodyText: string;
  readonly action: string;
  readonly palette: string;
};

const benefitFromContentful = (
  entry: ContentfulEntry<ContentfulSweetpassUpgradeBenefit>,
): SweetpassBenefitsBenefit => ({
  type: 'sweetpassBenefitsBenefit',
  id: entry.sys.id,
  image: {
    description: entry.fields.image.fields.description,
    url: entry.fields.image.fields.file.url,
  },
  titleText: entry.fields.titleText,
  bodyText: entry.fields.bodyText,
  action: entry.fields.action as SweetpassBenefitsBenefitAction,
});

// ––– Cards ––––––––––––––––––––––––––––

export type SweetpassBenefitsCardsSection = {
  readonly type: 'sweetpassBenefitsCardsSection';
  readonly id: string;
  readonly cards: readonly SweetpassBenefitsCardsCard[];
};

type ContentfulSweetpassBenefitsCards = {
  readonly content: ReadonlyArray<
    ContentfulEntry<ContentfulSweetpassBenefitsCard>
  >;
};

const cardsSectionFromContentful = (
  entry: ContentfulEntry<ContentfulSweetpassBenefitsCards>,
): SweetpassBenefitsCardsSection => ({
  type: 'sweetpassBenefitsCardsSection',
  id: entry.sys.id,
  cards: entry.fields.content.map(cardFromContentful),
});

// ––– Card CTA –––––––––––––––––––––––––

export type SweetpassBenefitsCardCtaAction = 'view-all' | 'edit';

export type SweetpassBenefitsCardCta = {
  readonly type: 'sweetpassBenefitsCardCta';
  readonly id: string;
  readonly title: string;
  readonly action: SweetpassBenefitsCardCtaAction;
};

type ContentfulSweetpassBenefitsCardCta = {
  readonly title: string;
  readonly action: string;
};

const cardCtaFromContentful = (
  entry: ContentfulEntry<ContentfulSweetpassBenefitsCardCta>,
): SweetpassBenefitsCardCta => ({
  type: 'sweetpassBenefitsCardCta',
  id: entry.sys.id,
  title: entry.fields.title,
  action: entry.fields.action as SweetpassBenefitsCardCta['action'],
});

// ––– Card –––––––––––––––––––––––––––––

export type SweetpassBenefitsPageCardPaletteTag = 'cucumber' | 'quinoa';

export type SweetpassBenefitsCardsCard = {
  readonly type: 'sweetpassBenefitsCard';
  readonly id: string;
  readonly title: string;
  readonly description: string;
  readonly cta: SweetpassBenefitsCardCta;
  readonly palette: SweetpassBenefitsPageCardPaletteTag;
};

type ContentfulSweetpassBenefitsCard = {
  readonly title: string;
  readonly description: string;
  readonly cta: ContentfulEntry<ContentfulSweetpassBenefitsCardCta>;
  readonly palette: string;
};

const cardFromContentful = (
  entry: ContentfulEntry<ContentfulSweetpassBenefitsCard>,
): SweetpassBenefitsCardsCard => ({
  type: 'sweetpassBenefitsCard',
  id: entry.sys.id,
  title: entry.fields.title,
  description: entry.fields.description,
  cta: cardCtaFromContentful(entry.fields.cta),
  palette: entry.fields.palette as SweetpassBenefitsPageCardPaletteTag,
});
